import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from '../../../services/toast-service';
import { TenantProvisioningService, ProvisionCustomerRequest, ProvisionCustomerResult, ModuleMenuTemplate } from '../../../services/tenant-provisioning.service';
import { MenuRouteRegistryService } from '../../../shared/menu/menu-route-registry.service';
import { SystemDomainService } from '../../../services/system-domain.service';
import { Module } from '../../../services/system-domain.service';
import { SystemModuleService } from '../../../services/system-module.service';
import { SystemUserAccountService, UserAccount } from '../../../services/system-user-account.service';
import { of, Subject, Subscription } from 'rxjs';
import { catchError, debounceTime, switchMap, distinctUntilChanged } from 'rxjs/operators';

interface ReviewData {
  customer: { name: string; isExisting: boolean; email?: string; phone?: string };
  domainName: string;
  databaseName: string;
  sqlServerType: string;
  adminUsername: string;
  adminFullName: string;
  moduleCount: number;
  features: {
    externalWorkOrders: boolean;
    stockIntegration: boolean;
    bugReport: boolean;
  };
}

@Component({
  selector: 'app-new-customer-wizard',
  templateUrl: './new-customer-wizard.component.html',
  styleUrls: ['./new-customer-wizard.component.scss']
})
export class NewCustomerWizardComponent implements OnInit, OnDestroy {
  // Wizard state
  currentStep = 1;
  totalSteps = 6;
  isProvisioning = false;
  provisioningResult: ProvisionCustomerResult | null = null;
  /** API-nøkkelen vises maskert med toggle. Domain-management bruker samme mønster
   *  for `domainAPIKey` (linje 168). Hindrer at nøkkelen henger åpent på skjermen
   *  hvis admin tar et screenshot av wizard-resultatet. */
  showApiKey = false;
  provisioningError: string | null = null;
  showSqlPassword = false;

  // Forms (one per step)
  customerForm!: FormGroup;
  domainForm!: FormGroup;
  databaseForm!: FormGroup;
  adminForm!: FormGroup;
  modulesForm!: FormGroup;

  // Data
  userAccounts: UserAccount[] = [];
  availableModules: Module[] = [];
  useExistingCustomer = false;

  // Cached step labels (avoid re-evaluating on every change detection)
  cachedStepLabels: string[] = [];
  cachedReviewData: ReviewData | null = null;

  // Validation state
  domainNameAvailable: boolean | null = null;
  dbNameAvailable: boolean | null = null;
  domainNameChecking = false;
  dbNameChecking = false;

  // Debounce subjects for async validation
  private domainNameCheck$ = new Subject<string>();
  private dbNameCheck$ = new Subject<string>();
  private subscriptions: Subscription[] = [];

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private translateService: TranslateService,
    private toastService: ToastService,
    private provisioningService: TenantProvisioningService,
    private systemDomainService: SystemDomainService,
    private systemModuleService: SystemModuleService,
    private userAccountService: SystemUserAccountService,
    private menuRouteRegistry: MenuRouteRegistryService
  ) {}

  ngOnInit(): void {
    this.initForms();
    this.loadData();
    this.setupAsyncValidation();
    this.updateStepLabels();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
    this.domainNameCheck$.complete();
    this.dbNameCheck$.complete();
  }

  private updateStepLabels(): void {
    this.cachedStepLabels = [
      this.translateService.instant('SystemAdmin.newCustomer.stepCustomer'),
      this.translateService.instant('SystemAdmin.newCustomer.stepDomain'),
      this.translateService.instant('SystemAdmin.newCustomer.stepDatabase'),
      this.translateService.instant('SystemAdmin.newCustomer.stepAdmin'),
      this.translateService.instant('SystemAdmin.newCustomer.stepModules'),
      this.translateService.instant('SystemAdmin.newCustomer.stepReview')
    ];
  }

  // ── Form initialization ────────────────────────────────────────────

  private initForms(): void {
    // Step 1: Customer info
    this.customerForm = this.fb.group({
      useExisting: [false],
      existingUserAccountID: [null],
      newCustomerName: ['', [Validators.maxLength(100)]],
      newCustomerAddress: ['', [Validators.maxLength(50)]],
      newCustomerZip: ['', [Validators.maxLength(6)]],
      newCustomerCity: ['', [Validators.maxLength(30)]],
      newCustomerEmail: ['', [Validators.required, Validators.email]],
      newCustomerPhone: ['', [Validators.maxLength(20)]]
    }, { validators: [this.customerValidator] });

    // Step 2: Domain config
    this.domainForm = this.fb.group({
      domainName: ['', [Validators.required, Validators.pattern(/^[a-z0-9][a-z0-9.\-]*[a-z0-9]$/)]],
      domainColors: [''],
      userLogo: [''],
      userBackground: [''],
      userLogoSmall: ['']
    });

    // Step 3: Database config
    this.databaseForm = this.fb.group({
      databaseName: ['wv_client_', [Validators.required, Validators.pattern(/^wv_client_[a-zA-Z0-9_]+$/)]],
      sqlServerType: ['OnPremise', Validators.required],
      sqlServerAddress: [''],
      sqlAdminLogin: [''],
      sqlAdminPassword: ['']
    });

    // Step 4: Admin user
    this.adminForm = this.fb.group({
      adminUsername: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(100)]],
      adminPassword: ['', [Validators.required, Validators.minLength(8), Validators.maxLength(100)]],
      adminPasswordConfirm: ['', Validators.required],
      adminFullName: ['', [Validators.required, Validators.maxLength(50)]],
      adminEmail: ['', [Validators.required, Validators.email]]
    }, { validators: [this.passwordMatchValidator] });

    // Step 5: Modules (checkboxes built dynamically)
    this.modulesForm = this.fb.group({
      useExternalWorkOrders: [false],
      useStockIntegration: [false],
      useBugReport: [false],
      selectedModules: [[]]
    });
  }

  // ── Custom validators ──────────────────────────────────────────────

  private customerValidator(group: AbstractControl): ValidationErrors | null {
    const useExisting = group.get('useExisting')?.value;
    if (useExisting) {
      const id = group.get('existingUserAccountID')?.value;
      if (!id || id <= 0) return { customerRequired: true };
    } else {
      const name = group.get('newCustomerName')?.value;
      if (!name || name.trim().length === 0) return { customerRequired: true };
    }
    return null;
  }

  private passwordMatchValidator(group: AbstractControl): ValidationErrors | null {
    const pass = group.get('adminPassword')?.value;
    const confirm = group.get('adminPasswordConfirm')?.value;
    if (pass && confirm && pass !== confirm) return { passwordMismatch: true };
    return null;
  }

  // ── Data loading ───────────────────────────────────────────────────

  private loadData(): void {
    this.subscriptions.push(
      this.userAccountService.getAllUserAccounts().subscribe({
        next: (response) => {
          if (response?.success && response.result) {
            this.userAccounts = response.result;
          }
        }
      })
    );

    this.subscriptions.push(
      this.systemModuleService.getAllModules().subscribe({
        next: (modules) => {
          this.availableModules = modules || [];
        }
      })
    );
  }

  // ── Async name validation ──────────────────────────────────────────

  private setupAsyncValidation(): void {
    this.subscriptions.push(
      this.domainNameCheck$.pipe(
        debounceTime(500),
        distinctUntilChanged(),
        switchMap(name => {
          this.domainNameChecking = true;
          return this.provisioningService.validateDomainName(name).pipe(
            catchError(() => {
              this.domainNameAvailable = null;
              this.domainNameChecking = false;
              return of(null);
            })
          );
        })
      ).subscribe({
        next: (available) => {
          if (available !== null) {
            this.domainNameAvailable = available;
            this.domainNameChecking = false;
          }
        }
      })
    );

    this.subscriptions.push(
      this.dbNameCheck$.pipe(
        debounceTime(500),
        distinctUntilChanged(),
        switchMap(name => {
          this.dbNameChecking = true;
          return this.provisioningService.validateDbName(name).pipe(
            catchError(() => {
              this.dbNameAvailable = null;
              this.dbNameChecking = false;
              return of(null);
            })
          );
        })
      ).subscribe({
        next: (available) => {
          if (available !== null) {
            this.dbNameAvailable = available;
            this.dbNameChecking = false;
          }
        }
      })
    );
  }

  onDomainNameChange(): void {
    const name = this.domainForm.get('domainName')?.value;
    if (name && name.length >= 3) {
      this.domainNameAvailable = null;
      this.domainNameCheck$.next(name);
    } else {
      this.domainNameAvailable = null;
    }
  }

  onDbNameChange(): void {
    const name = this.databaseForm.get('databaseName')?.value;
    if (name && name.length > 10) {
      this.dbNameAvailable = null;
      this.dbNameCheck$.next(name);
    } else {
      this.dbNameAvailable = null;
    }
  }

  // ── Customer type toggle ───────────────────────────────────────────

  onCustomerTypeChange(): void {
    this.useExistingCustomer = this.customerForm.get('useExisting')?.value;
  }

  // ── Auto-generate DB name from domain name ─────────────────────────

  onDomainNameBlur(): void {
    const domainName = this.domainForm.get('domainName')?.value;
    const dbName = this.databaseForm.get('databaseName')?.value;

    // Auto-fill DB name if it's still the default prefix
    if (domainName && (!dbName || dbName === 'wv_client_')) {
      const sanitized = domainName.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase();
      this.databaseForm.patchValue({ databaseName: `wv_client_${sanitized}` });
      this.onDbNameChange();
    }
  }

  // ── Step navigation ────────────────────────────────────────────────

  get stepLabels(): string[] {
    return this.cachedStepLabels;
  }

  canGoNext(): boolean {
    switch (this.currentStep) {
      case 1: return this.customerForm.valid;
      case 2: return this.domainForm.valid && this.domainNameAvailable === true;
      case 3: return this.databaseForm.valid && this.dbNameAvailable === true;
      case 4: return this.adminForm.valid;
      case 5: return true; // Modules are optional
      default: return true;
    }
  }

  nextStep(): void {
    if (this.currentStep < this.totalSteps && this.canGoNext()) {
      // Trigger auto-fill when leaving domain step
      if (this.currentStep === 2) {
        this.onDomainNameBlur();
      }
      this.currentStep++;
      // Cache review data when entering the review step
      if (this.currentStep === this.totalSteps) {
        this.cachedReviewData = this.buildReviewData();
      }
    }
  }

  previousStep(): void {
    if (this.currentStep > 1) {
      this.currentStep--;
      this.cachedReviewData = null; // Invalidate review cache on navigation
    }
  }

  goToStep(step: number): void {
    // Only allow going to steps that have been completed or the current step
    if (step <= this.currentStep && step >= 1) {
      this.currentStep = step;
      this.cachedReviewData = null; // Invalidate review cache on navigation
    }
  }

  // ── Module selection ───────────────────────────────────────────────

  toggleModule(moduleId: number): void {
    const selected: number[] = this.modulesForm.get('selectedModules')?.value || [];
    const index = selected.indexOf(moduleId);
    if (index >= 0) {
      selected.splice(index, 1);
    } else {
      selected.push(moduleId);
    }
    this.modulesForm.patchValue({ selectedModules: [...selected] });
  }

  isModuleSelected(moduleId: number): boolean {
    const selected: number[] = this.modulesForm.get('selectedModules')?.value || [];
    return selected.includes(moduleId);
  }

  // ── Review data ────────────────────────────────────────────────────

  get reviewData(): ReviewData {
    return this.cachedReviewData || this.buildReviewData();
  }

  private buildReviewData(): ReviewData {
    const customer = this.customerForm.value;
    const domain = this.domainForm.value;
    const db = this.databaseForm.value;
    const admin = this.adminForm.value;
    const modules = this.modulesForm.value;

    return {
      customer: customer.useExisting
        ? { name: this.getSelectedAccountName(), isExisting: true }
        : { name: customer.newCustomerName, isExisting: false, email: customer.newCustomerEmail, phone: customer.newCustomerPhone },
      domainName: domain.domainName,
      databaseName: db.databaseName,
      sqlServerType: db.sqlServerType,
      adminUsername: admin.adminUsername,
      adminFullName: admin.adminFullName,
      moduleCount: (modules.selectedModules || []).length,
      features: {
        externalWorkOrders: modules.useExternalWorkOrders,
        stockIntegration: modules.useStockIntegration,
        bugReport: modules.useBugReport
      }
    };
  }

  private getSelectedAccountName(): string {
    const id = this.customerForm.get('existingUserAccountID')?.value;
    const account = this.userAccounts.find(a => a.userAccountID === id);
    return account?.fullName || `ID: ${id}`;
  }

  // ── Provision ──────────────────────────────────────────────────────

  startProvisioning(): void {
    if (this.isProvisioning) return;

    this.isProvisioning = true;
    this.provisioningError = null;
    this.provisioningResult = null;

    const request = this.buildRequest();

    this.provisioningService.provision(request).subscribe({
      next: (result) => {
        this.provisioningResult = result;
        this.isProvisioning = false;
        if (result?.success) {
          this.toastService.show({
            type: 'success',
            data: [this.translateService.instant('SystemAdmin.newCustomer.provisionSuccess')]
          });
        } else {
          this.provisioningError = result?.steps?.slice(-1)?.[0] || 'Unknown error';
        }
      },
      error: (error) => {
        this.isProvisioning = false;
        this.provisioningError = error?.error?.errorMessage?.[0]?.message || error?.message || 'Provisioning failed';
        this.toastService.show({
          type: 'error',
          data: [this.translateService.instant('SystemAdmin.newCustomer.provisionError')]
        });
      }
    });
  }

  private buildRequest(): ProvisionCustomerRequest {
    const customer = this.customerForm.value;
    const domain = this.domainForm.value;
    const db = this.databaseForm.value;
    const admin = this.adminForm.value;
    const modules = this.modulesForm.value;

    // Bundle default menu templates for each selected module so the
    // provisioning service can seed menus inside the tenant transaction.
    const selectedModuleIds: number[] = modules.selectedModules || [];
    const menuTemplates: ModuleMenuTemplate[] = selectedModuleIds
      .map(id => this.menuRouteRegistry.getTemplate(id))
      .filter((t): t is NonNullable<typeof t> => !!t)
      .map(t => ({ moduleId: t.moduleId, roots: t.roots }));

    return {
      domainName: domain.domainName,
      databaseName: db.databaseName,
      sqlServerType: db.sqlServerType,
      sqlServerAddress: db.sqlServerAddress || undefined,
      sqlAdminLogin: db.sqlAdminLogin || undefined,
      sqlAdminPassword: db.sqlAdminPassword || undefined,
      existingUserAccountID: customer.useExisting ? customer.existingUserAccountID : undefined,
      newCustomerName: !customer.useExisting ? customer.newCustomerName : undefined,
      newCustomerAddress: !customer.useExisting ? customer.newCustomerAddress : undefined,
      newCustomerZip: !customer.useExisting ? customer.newCustomerZip : undefined,
      newCustomerCity: !customer.useExisting ? customer.newCustomerCity : undefined,
      newCustomerEmail: !customer.useExisting ? customer.newCustomerEmail : undefined,
      newCustomerPhone: !customer.useExisting ? customer.newCustomerPhone : undefined,
      adminUsername: admin.adminUsername,
      adminPassword: admin.adminPassword,
      adminFullName: admin.adminFullName,
      adminEmail: admin.adminEmail || undefined,
      domainColors: domain.domainColors || undefined,
      userLogo: domain.userLogo || undefined,
      userBackground: domain.userBackground || undefined,
      userLogoSmall: domain.userLogoSmall || undefined,
      useExternalWorkOrders: modules.useExternalWorkOrders,
      useStockIntegration: modules.useStockIntegration,
      useBugReport: modules.useBugReport,
      activeModuleIDs: selectedModuleIds,
      menuTemplates: menuTemplates.length > 0 ? menuTemplates : undefined
    };
  }

  // ── Navigation after completion ────────────────────────────────────

  copyToClipboard(text: string | undefined): void {
    if (!text) return;
    navigator.clipboard.writeText(text);
  }

  goToDomainManagement(): void {
    this.router.navigate(['/system-admin/domains']);
  }

  goBackToReview(): void {
    this.provisioningResult = null;
    this.provisioningError = null;
    this.currentStep = this.totalSteps; // Back to review step
  }

  startNewProvisioning(): void {
    this.currentStep = 1;
    this.provisioningResult = null;
    this.provisioningError = null;
    this.domainNameAvailable = null;
    this.dbNameAvailable = null;
    this.cachedReviewData = null;
    this.initForms();
  }
}
